// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
import { Controller } from "@hotwired/stimulus"

/**
 * Tabs Controller
 * @gem rapid_rails_ui
 *
 * WAI-ARIA APG tabs pattern: roving tabindex, aria-selected, arrow-key
 * navigation (wrap-around, disabled tabs skipped), automatic/manual
 * activation, optional URL-hash deep-linking, fade-in panel reveal gated by
 * prefers-reduced-motion.
 *
 * Which arrow keys move focus (left/right vs up/down) is decided in Ruby via
 * the `data-action` attribute rendered per orientation — this controller
 * never branches on orientation itself. That keeps a horizontal tablist from
 * ever seeing an Up/Down binding and vice versa, straight from the APG
 * keyboard table.
 *
 * Targets: tab, panel
 *
 * Values:
 *   activation (String, default "automatic") — "automatic" | "manual"
 *   deepLink (Boolean, default false)
 *   animation (String, default "fade") — "fade" | "none"
 *
 * Per-tab data attributes (not Stimulus Values — these vary per element,
 * e.g. per-tab color override, so they can't live on the single controller
 * instance):
 *   data-<identifier>-tab-id      — the raw tab id (deep-link hash + change event detail)
 *   data-<identifier>-active-class   — COMPLETE class string for this tab's active state
 *   data-<identifier>-inactive-class — COMPLETE class string for this tab's inactive state
 *
 * Dispatches `<identifier>:change` (`tabs:change` bare, `rui--tabs:change` namespaced) (detail: { tab: <raw id> }) on every switch.
 */
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = {
    activation: { type: String, default: "automatic" },
    deepLink: { type: Boolean, default: false },
    animation: { type: String, default: "fade" }
  }

  connect() {
    this._reducedMotion = this._matchesReducedMotion()

    if (this.deepLinkValue) {
      this._hashChangeHandler = () => this._activateFromHash()
      window.addEventListener("hashchange", this._hashChangeHandler)
      this._activateFromHash()
    }
  }

  disconnect() {
    if (this._hashChangeHandler) {
      window.removeEventListener("hashchange", this._hashChangeHandler)
    }
  }

  // ==========================================================================
  // PUBLIC ACTIONS
  // ==========================================================================

  select(event) {
    this._activate(event.currentTarget, { updateHash: true })
  }

  next(event) {
    event.preventDefault()
    this._moveFocus(1, event.currentTarget)
  }

  prev(event) {
    event.preventDefault()
    this._moveFocus(-1, event.currentTarget)
  }

  firstTab(event) {
    event.preventDefault()
    const tabs = this._enabledTabs()
    if (tabs.length > 0) this._focusAndMaybeActivate(tabs[0])
  }

  lastTab(event) {
    event.preventDefault()
    const tabs = this._enabledTabs()
    if (tabs.length > 0) this._focusAndMaybeActivate(tabs[tabs.length - 1])
  }

  // ==========================================================================
  // INTERNALS
  // ==========================================================================

  _moveFocus(direction, current) {
    const tabs = this._enabledTabs()
    if (tabs.length === 0) return

    const currentIndex = tabs.indexOf(current)
    const from = currentIndex === -1 ? 0 : currentIndex
    const next = (from + direction + tabs.length) % tabs.length
    this._focusAndMaybeActivate(tabs[next])
  }

  // Arrow/Home/End always move roving focus. In "automatic" activation that
  // also selects the tab immediately; in "manual" activation the tab only
  // gains focus + tabindex="0" — the native click a subsequent Enter/Space
  // fires on a focused <button> is what actually selects it (no separate
  // keydown.enter/space binding needed).
  _focusAndMaybeActivate(tab) {
    tab.focus({ preventScroll: true })

    if (this.activationValue === "manual") {
      this.tabTargets.forEach((t) => t.setAttribute("tabindex", t === tab ? "0" : "-1"))
      return
    }

    this._activate(tab, { updateHash: true })
  }

  _activate(tab, { updateHash = false } = {}) {
    if (!tab || tab.getAttribute("aria-disabled") === "true") return

    this.tabTargets.forEach((t) => {
      const isTarget = t === tab
      t.setAttribute("aria-selected", isTarget ? "true" : "false")
      t.setAttribute("tabindex", isTarget ? "0" : "-1")
      this._applyStateClasses(t, isTarget)
    })

    const panelId = tab.getAttribute("aria-controls")
    const targetPanel = panelId ? document.getElementById(panelId) : null

    this.panelTargets.forEach((panel) => {
      if (panel === targetPanel) {
        this._revealPanel(panel)
      } else {
        panel.hidden = true
        this._resetPanelAnimationState(panel)
      }
    })

    const tabId = tab.getAttribute(this._attr("tab-id"))

    if (updateHash && this.deepLinkValue && tabId) {
      history.replaceState(null, "", `#${tabId}`)
    }

    this.dispatch("change", { detail: { tab: tabId } })
  }

  // Attribute names are relative to this controller's registered identifier
  // ("tabs" bare, "rui--tabs" under config.stimulus_namespace) — they must
  // match what the Ruby side emits via stimulus_attr for ANY namespace.
  _attr(suffix) {
    return `data-${this.identifier}-${suffix}`
  }

  _applyStateClasses(tab, isActive) {
    const attr = this._attr(isActive ? "active-class" : "inactive-class")
    const classes = tab.getAttribute(attr)
    if (classes) tab.className = classes
  }

  _revealPanel(panel) {
    panel.hidden = false

    if (this.animationValue !== "fade" || this._motionSuppressed()) {
      this._resetPanelAnimationState(panel)
      return
    }

    panel.classList.remove("opacity-100")
    panel.classList.add("opacity-0")
    // Force a reflow so the browser registers the opacity-0 starting state
    // before the next frame flips it — otherwise the two class changes
    // coalesce into one paint and no transition is visible.
    void panel.offsetWidth
    requestAnimationFrame(() => {
      panel.classList.remove("opacity-0")
      panel.classList.add("opacity-100")
    })
  }

  _resetPanelAnimationState(panel) {
    panel.classList.remove("opacity-0", "opacity-100")
  }

  _activateFromHash() {
    const raw = window.location.hash.replace(/^#/, "")
    if (!raw) return

    const match = this.tabTargets.find((t) => t.getAttribute(this._attr("tab-id")) === raw)
    if (match) this._activate(match, { updateHash: false })
  }

  _enabledTabs() {
    return this.tabTargets.filter((t) => t.getAttribute("aria-disabled") !== "true")
  }

  _matchesReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // Single source of truth for "should the fade animation be suppressed right
  // now." Every motion gate must route through here — see the carousel
  // controller's identical convention (0.50.4) for why (a bare reduced-motion
  // field check scattered at each call site is how that component's
  // reduced-motion opt-out silently broke in <=0.50.3). Tabs has no consumer
  // opt-out value (unlike carousel's respectReducedMotionValue), so this
  // simply mirrors the OS preference — but every animation branch still
  // funnels through this one method rather than reading the underlying field
  // directly.
  _motionSuppressed() {
    return this._reducedMotion
  }
}
